import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridReadyEvent, SideBarDef, SizeColumnsToFitGridStrategy } from 'ag-grid-community';
import { AG_GRID_LOCALE_DE } from '../../_helpers/aggrid.locale.de';
import { AgGridThemeService } from '../../_services/ag-grid-theme.service';
import { PickpointStationService } from '../../_services/pickpoint-station.service';
import { PickpointStation } from '../../_models/shipping-config.interface';
import { RecordNavigationComponent } from '../_shared/record-navigation/record-navigation.component';
import { AgGridGenericLinkRenderer } from '../_shared/ag-grid-renderer/ag-grid-generic-link.component';

@Component({
  standalone: true,
  imports: [CommonModule, RouterModule, AgGridAngular, RecordNavigationComponent],
  selector: 'hawk-pickpoint-station-index',
  template: `
    <hawk-record-navigation
      [breadcrumbLinkList]="[{ link: '/a/lager/uebersicht', description: 'Lager' }]"
      [root]="'warehouse'"
      [leafDescription]="'PickPoint-Stationen'"
      class="app-page"
    >
      <div slot="toolbar" class="flex flex-row gap-2 items-center">
        <input
          type="text"
          class="bm-input"
          placeholder="Schnellfilter..."
          (input)="onQuickFilter($event)"
        />
        <button class="bm-btn bm-btn-primary" (click)="openImportDialog()">
          <i class="fa fa-upload"></i>
          Import
        </button>
      </div>
      <div class="page-content px-4 pb-2">
        <ag-grid-angular
          class="ag-theme-quartz h-full"
          [theme]="hawkTheme"
          [localeText]="localeText"
          [rowData]="rowData()"
          [columnDefs]="columnDefs"
          [defaultColDef]="defaultColDef"
          [autoSizeStrategy]="autoSizeStrategy"
          [rowSelection]="rowSelection"
          [pagination]="true"
          [paginationPageSize]="100"
          [paginationPageSizeSelector]="[25, 100, 250, 1000]"
          [sideBar]="sideBarOptions"
          (gridReady)="onGridReady($event)"
          (rowClicked)="onRowClicked($event)"
        />
      </div>
    </hawk-record-navigation>

    <!-- Import-Dialog -->
    @if (showImport()) {
      <div class="fixed inset-0 bg-black/30 z-50 flex items-center justify-center" (click)="showImport.set(false)">
        <div class="bm-card bm-card-border bg-base-100 w-96" (click)="$event.stopPropagation()">
          <div class="bm-card-body">
            <div class="bm-card-title">PickPoint-Stationen importieren</div>
            <input type="file" accept=".txt,.csv" class="bm-input w-full" (change)="onFileSelected($event)" />
            @if (importResult()) {
              <div class="bm-alert bm-alert-soft mt-2"
                [class.bm-alert-success]="importResult()!.success"
                [class.bm-alert-error]="!importResult()!.success">
                <span>{{ importResult()!.created }} neu, {{ importResult()!.updated }} aktualisiert</span>
              </div>
            }
            <div class="flex gap-2 mt-3">
              <button class="bm-btn bm-btn-primary bm-btn-sm" (click)="executeImport()" [disabled]="!importFile()">
                Importieren
              </button>
              <button class="bm-btn bm-btn-ghost bm-btn-sm" (click)="showImport.set(false)">Schliessen</button>
            </div>
          </div>
        </div>
      </div>
    }
  `,
  host: {
    class: 'app-page'
  }
})
export class PickpointStationIndexComponent implements OnInit {
  public hawkTheme = inject(AgGridThemeService).getTheme();
  public localeText = AG_GRID_LOCALE_DE;
  public rowData = signal<PickpointStation[]>([]);
  public showImport = signal(false);
  public importFile = signal<File | null>(null);
  public importResult = signal<{ success: boolean; created: number; updated: number } | null>(null);

  public rowSelection: 'single' = 'single';
  public sideBarOptions: SideBarDef = {
    hiddenByDefault: false,
    toolPanels: ['columns', 'filters']
  };
  public defaultColDef: ColDef = {
    filter: true,
    sortable: true,
    resizable: true,
  };
  public autoSizeStrategy: SizeColumnsToFitGridStrategy = { type: 'fitGridWidth' };

  private gridApi: any;

  columnDefs: ColDef[] = [
    {
      field: 'pp_id',
      headerName: 'PP-ID',
      width: 100,
      sort: 'asc',
      cellRenderer: AgGridGenericLinkRenderer,
      cellRendererParams: {
        labelFromData: true,
        dataKey: 'pp_id',
        routerLink: ['/', 'pickpoint_stations', '@id'],
      },
    },
    { field: 'name', headerName: 'Name', flex: 1 },
    { field: 'address_street', headerName: 'Strasse', width: 200 },
    { field: 'address_zip', headerName: 'PLZ', width: 80 },
    { field: 'address_city', headerName: 'Stadt', width: 150 },
    { field: 'opening_hours', headerName: 'Oeffnungszeiten', width: 200 },
    {
      field: 'active', headerName: 'Status', width: 90,
      cellRenderer: (params: any) => params.value
        ? '<span class="bm-badge bm-badge-xs bm-badge-success">Aktiv</span>'
        : '<span class="bm-badge bm-badge-xs bm-badge-error">Inaktiv</span>'
    },
    {
      headerName: 'Aktion',
      width: 130,
      sortable: false,
      filter: false,
      // Klick toggled active. stopPropagation, damit der Row-Click (Detail-
      // Navigation) nicht zusaetzlich feuert.
      cellRenderer: (p: { data?: PickpointStation }) =>
        p.data?.active
          ? '<button class="bm-btn bm-btn-xs bm-btn-warning">Deaktivieren</button>'
          : '<button class="bm-btn bm-btn-xs bm-btn-success">Aktivieren</button>',
      onCellClicked: (event: any) => {
        event.event?.stopPropagation();
        const row = event.data as PickpointStation | undefined;
        if (!row) return;
        this.toggleActive(row);
      },
      cellStyle: { textAlign: 'center', cursor: 'pointer' },
    },
  ];

  constructor(
    private stationService: PickpointStationService,
    private router: Router,
  ) {}

  ngOnInit() {
    this.loadData();
  }

  loadData() {
    this.stationService.getStations().subscribe(data => this.rowData.set(data));
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
  }

  onQuickFilter(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.gridApi?.setGridOption('quickFilterText', value);
  }

  onRowClicked(event: any) {
    // Klick auf die Aktion-Cell wird dort separat behandelt (siehe colDef
    // Aktion → onCellClicked) und stopPropagation'd, sodass die Navigation
    // hier nicht ausgeloest wird.
    if (event.data?.id) {
      this.router.navigate(['/a/pickpoint_stations', event.data.id]);
    }
  }

  toggleActive(row: PickpointStation) {
    this.stationService.toggleActive(row.id, !row.active).subscribe({
      next: (updated) => {
        // Lokal in rowData ersetzen statt Reload — Grid aktualisiert sich
        // ueber Object-Identity automatisch, sortierung/filter bleiben.
        this.rowData.update((rows) => rows.map((r) => (r.id === updated.id ? updated : r)));
      },
      error: (err) => {
        console.error('Status-Wechsel fehlgeschlagen', err);
      },
    });
  }

  openImportDialog() {
    this.showImport.set(true);
    this.importResult.set(null);
    this.importFile.set(null);
  }

  onFileSelected(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    this.importFile.set(file ?? null);
  }

  async executeImport() {
    const file = this.importFile();
    if (!file) return;

    try {
      const text = await this.readFileAsText(file);
      const stations = this.parseTxtFile(text);
      const result = await this.stationService.importStations(stations);
      this.importResult.set(result);
      this.loadData();
    } catch (e) {
      console.error('Import fehlgeschlagen', e);
    }
  }

  // Liest die Datei zuerst als UTF-8; falls ungueltige Bytes auftreten
  // (PickPoint exportiert i.d.R. in Windows-1252), wird auf windows-1252 zurueckgefallen.
  private async readFileAsText(file: File): Promise<string> {
    const buffer = await file.arrayBuffer();
    try {
      return new TextDecoder('utf-8', { fatal: true }).decode(buffer);
    } catch {
      return new TextDecoder('windows-1252').decode(buffer);
    }
  }

  // Parst die Tab-separierte PickPoint TXT-Datei
  private parseTxtFile(text: string): Partial<PickpointStation>[] {
    const lines = text.split('\n').filter(l => l.trim().length > 0);
    if (lines.length < 2) return [];

    // Header ueberspringen
    return lines.slice(1).map(line => {
      const cols = line.split('\t');
      const cancellationDate = cols[6]?.trim() || undefined;
      return {
        pp_id: cols[0]?.trim(),
        name: cols[1]?.trim(),
        address_street: cols[2]?.trim(),
        address_zip: cols[3]?.trim(),
        address_city: cols[4]?.trim(),
        activation_date: cols[5]?.trim() || undefined,
        cancellation_date: cancellationDate,
        opening_hours: cols[7]?.trim(),
        report_type: cols[8]?.trim(),
        address_longitude: cols[9]?.trim() ? parseFloat(cols[9].trim()) : undefined,
        address_latitude: cols[10]?.trim() ? parseFloat(cols[10].trim()) : undefined,
        address_country: 'DE',
      } as Partial<PickpointStation>;
    }).filter(s => s.pp_id && s.name);
  }
}
